/**
 * Replays queued offline actions against Supabase.
 */
import { supabase } from "@/integrations/supabase/client";
import { getAll, remove, updateRetries, getBlob, removeBlob, type PendingAction } from "./offlineQueue";
import type { CheckinPayload, CheckoutPayload, PhotoPayload, TaskUpdatePayload, VisitNotePayload } from "@/types/database";

const MAX_RETRIES = 5;

async function replayAction(action: PendingAction): Promise<boolean> {
  const p = action.payload;

  try {
    switch (action.type) {
      case "checkin": {
        const cp = p as unknown as CheckinPayload;
        const { error } = await supabase
          .from("visits")
          .update({
            status: "em_andamento",
            checkin_at: cp.checkin_at,
            checkin_lat: cp.checkin_lat,
            checkin_lng: cp.checkin_lng,
            checkin_distance_meters: cp.checkin_distance_meters,
            checkin_battery: cp.checkin_battery,
          })
          .eq("id", cp.visit_id);
        if (error) throw error;
        return true;
      }
      case "checkout": {
        const co = p as unknown as CheckoutPayload;
        const { error } = await supabase
          .from("visits")
          .update({
            status: "concluida",
            checkout_at: co.checkout_at,
            checkout_lat: co.checkout_lat,
            checkout_lng: co.checkout_lng,
            checkout_distance_meters: co.checkout_distance_meters,
            checkout_battery: co.checkout_battery,
            checkout_reason: co.checkout_reason,
            notes: co.notes,
            time_in_store_minutes: co.time_in_store_minutes,
            forced_checkout: (co as any).forced_checkout ?? false,
          })
          .eq("id", co.visit_id);
        if (error) throw error;
        return true;
      }
      case "photo": {
        const pp = p as unknown as PhotoPayload;
        const blob = await getBlob(action.id);
        if (!blob) {
          console.warn("[offlineSync] Blob not found for photo action:", action.id);
          return true;
        }
        const { error: uploadError } = await supabase.storage
          .from(pp.bucket)
          .upload(pp.path, blob, { contentType: blob.type });
        if (uploadError) throw uploadError;

        const record = pp.record as Record<string, string>;
        const insertData = { ...record, photo_url: pp.path };
        const { error: dbError } = await (supabase.from as any)(pp.table).insert(insertData);
        if (dbError) throw dbError;

        await removeBlob(action.id);
        return true;
      }
      case "task_update": {
        const tp = p as unknown as TaskUpdatePayload;
        const { error } = await supabase
          .from("visit_tasks")
          .update({
            completed: tp.completed,
            completed_at: tp.completed_at,
            notes: tp.notes,
            reason_not_done: tp.reason_not_done,
          })
          .eq("id", tp.task_id);
        if (error) throw error;
        return true;
      }
      case "visit_note": {
        const vn = p as unknown as VisitNotePayload;
        const { error } = await supabase
          .from("visits")
          .update({ notes: vn.notes })
          .eq("id", vn.visit_id);
        if (error) throw error;
        return true;
      }
      default:
        console.warn("[offlineSync] Unknown action type:", action.type);
        return true; // remove unknown actions
    }
  } catch (err) {
    console.error("[offlineSync] Failed to replay action:", action.id, err);
    return false;
  }
}

export async function syncPendingActions(): Promise<{ synced: number; failed: number }> {
  const actions = await getAll();
  let synced = 0;
  let failed = 0;

  for (const action of actions) {
    if (action.retries >= MAX_RETRIES) {
      await remove(action.id);
      failed++;
      continue;
    }

    const ok = await replayAction(action);
    if (ok) {
      await remove(action.id);
      synced++;
    } else {
      await updateRetries(action.id);
      failed++;
    }
  }

  return { synced, failed };
}
